/**
 * HISTORY TAB (scenario scope only)
 *
 * Chronological audit-log timeline: every status change and bug link/unlink
 * event for a scenario. Rendered as a compact list — action badge on the left,
 * details on the right, muted timestamp underneath.
 *
 * Users: the backend stores only a Jira accountId per audit entry. We
 * prefetch full user details (display name + avatar) via the shared
 * userCache utility, re-rendering once the cache fills.
 */

import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Spinner from '@atlaskit/spinner'
import Avatar from '@atlaskit/avatar'
import Lozenge from '@atlaskit/lozenge'
import { token } from '@atlaskit/tokens'

import {
  fetchScenarioHistoryAsync,
  selectHistory,
  selectScenarioReport,
  selectReportLoading,
  selectReportErrors
} from '../../../store/slices/reportsSlice'
import { prefetchUsers, getCachedUserDetails } from '../../../utils/userCache'

/**
 * Map the scenario's runs (ordered by creation) to 1-based ordinals.
 * Mirrors the numbering shown on the scenario detail page (`01`, `02`, …).
 */
const useRunOrdinalMap = () => {
  const scenarioReport = useSelector(selectScenarioReport)
  return useMemo(() => {
    const map = new Map()
    const runs = scenarioReport?.runBreakdown || []
    runs.forEach((r, idx) => {
      if (r.runId != null) map.set(r.runId, idx + 1)
    })
    return map
  }, [scenarioReport])
}

const formatRunLabel = (ordinal) =>
  typeof ordinal === 'number' ? `Run ${String(ordinal).padStart(2, '0')}` : null

const ACTION_APPEARANCE = {
  CREATED: 'new',
  UPDATED: 'inprogress',
  CLEARED: 'default',
  BUG_LINKED: 'removed',
  BUG_UNLINKED: 'moved'
}

const formatAction = (action) => {
  if (!action) return ''
  return action.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
}

const formatDate = (value) => {
  if (!value) return ''
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

const entryBody = (entry) => {
  switch (entry.action) {
    case 'BUG_LINKED':
      return (
        <span>
          Linked bug{' '}
          <strong style={{ color: token('color.text', '#172B4D') }}>{entry.jiraIssueKey}</strong>
        </span>
      )
    case 'BUG_UNLINKED':
      return (
        <span>
          Unlinked bug{' '}
          <strong style={{ color: token('color.text', '#172B4D') }}>{entry.jiraIssueKey}</strong>
        </span>
      )
    case 'CLEARED':
      return (
        <span>
          Cleared status from{' '}
          <strong style={{ color: token('color.text', '#172B4D') }}>{entry.previousStatus || '—'}</strong>
        </span>
      )
    case 'CREATED':
      return (
        <span>
          Set status to{' '}
          <strong style={{ color: token('color.text', '#172B4D') }}>{entry.newStatus || '—'}</strong>
        </span>
      )
    case 'UPDATED':
    default:
      return (
        <span>
          Status{' '}
          <strong style={{ color: token('color.text', '#172B4D') }}>
            {entry.previousStatus || '—'}
          </strong>{' '}
          → {' '}
          <strong style={{ color: token('color.text', '#172B4D') }}>
            {entry.newStatus || '—'}
          </strong>
        </span>
      )
  }
}

const HistoryTab = ({ scenarioId }) => {
  const dispatch = useDispatch()
  const data = useSelector(selectHistory)
  const loading = useSelector(selectReportLoading)
  const errors = useSelector(selectReportErrors)

  // Bumped once the user-cache bulk fetch resolves so memoized maps below re-derive.
  const [userCacheVersion, setUserCacheVersion] = useState(0)

  const runOrdinalByRunId = useRunOrdinalMap()

  useEffect(() => {
    if (scenarioId) {
      dispatch(fetchScenarioHistoryAsync({ scenarioId }))
    }
  }, [dispatch, scenarioId])

  // Prefetch Jira user details for every unique author in the timeline.
  useEffect(() => {
    const ids = (data?.entries || [])
      .map((e) => e.changedByAccountId)
      .filter(Boolean)
    const uniqueIds = [...new Set(ids)]
    if (uniqueIds.length > 0) {
      prefetchUsers(uniqueIds).then(() => {
        setUserCacheVersion((v) => v + 1)
      })
    }
  }, [data])

  // Build a resolved view of each entry with displayName + avatarUrl attached.
  const entries = useMemo(() => {
    const rows = data?.entries || []
    return rows.map((e) => {
      const cached = getCachedUserDetails(e.changedByAccountId)
      return {
        ...e,
        displayName: cached?.displayName || 'Unknown User',
        avatarUrl: cached?.avatarUrl || null
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, userCacheVersion])

  if (loading.history) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <Spinner size="large" />
      </div>
    )
  }

  if (errors.history) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: token('color.text.danger', '#DE350B') }}>
        <p>Failed to load history</p>
        <p style={{ fontSize: '12px', color: token('color.text.subtle', '#6B778C') }}>{errors.history}</p>
      </div>
    )
  }

  if (entries.length === 0) {
    return (
      <div
        style={{
          padding: '32px',
          textAlign: 'center',
          color: token('color.text.subtle', '#6B778C'),
          fontSize: '13px',
          border: `1px dashed ${token('color.border', '#DFE1E6')}`,
          borderRadius: '4px'
        }}
      >
        No history recorded yet for this scenario.
      </div>
    )
  }

  return (
    <div
      style={{
        border: `1px solid ${token('color.border', '#DFE1E6')}`,
        borderRadius: '4px',
        overflow: 'hidden'
      }}
    >
      {entries.map((entry, idx) => (
        <div
          key={entry.id}
          style={{
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '16px',
            backgroundColor:
              idx % 2 === 0
                ? token('elevation.surface', '#FFFFFF')
                : token('color.background.neutral.subtle', '#FAFBFC'),
            borderBottom:
              idx < entries.length - 1
                ? `1px solid ${token('color.border', '#DFE1E6')}`
                : 'none'
          }}
        >
          <div style={{ flex: '0 0 auto', paddingTop: '2px' }}>
            <Lozenge appearance={ACTION_APPEARANCE[entry.action] || 'default'}>
              {formatAction(entry.action)}
            </Lozenge>
          </div>

          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <div
              style={{
                fontSize: '13px',
                color: token('color.text', '#172B4D'),
                lineHeight: 1.4
              }}
            >
              {entryBody(entry)}
            </div>
            <div
              style={{
                marginTop: '6px',
                fontSize: '12px',
                color: token('color.text.subtle', '#6B778C'),
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'wrap'
              }}
            >
              {entry.changedByAccountId && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <Avatar size="xsmall" src={entry.avatarUrl} name={entry.displayName} />
                  <span>{entry.displayName}</span>
                </span>
              )}
              {entry.testRunId && runOrdinalByRunId.get(entry.testRunId) && (
                <span>· {formatRunLabel(runOrdinalByRunId.get(entry.testRunId))}</span>
              )}
              <span>· {formatDate(entry.changedOn)}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

export default HistoryTab
